import React, { useState, useEffect } from 'react';
import { ArrowRight, LogIn } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from './Logo';
import { trackButtonClick } from '../lib/analytics';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [glowing, setGlowing] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlowing(true);
      setTimeout(() => setGlowing(false), 1200);
    }, 10000);
    const initial = setTimeout(() => {
      setGlowing(true);
      setTimeout(() => setGlowing(false), 1200);
    }, 3000);
    return () => { clearInterval(interval); clearTimeout(initial); };
  }, []);

  const handleLoginClick = () => {
    if (isAuthenticated && user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.company_id) {
        navigate(`/portal/${user.company_id}`);
      } else {
        navigate('/onboarding');
      }
    } else {
      navigate('/login');
    }
  };

  return (
    <header className="fixed top-0 w-full z-50 flex justify-center px-4 pt-4">
      <div
        className={`transition-all duration-500 flex items-center gap-3 sm:gap-5 px-4 sm:px-5 h-[48px] ${
          isScrolled
            ? 'bg-black/90 backdrop-blur-xl shadow-lg shadow-black/20 rounded-full border border-white/5 w-auto'
            : 'bg-black/80 backdrop-blur-md rounded-full border border-white/5 w-auto'
        }`}
      >
        <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <Logo height={28} />
        </div>

        <Link
          to="/blog"
          className="flex items-center px-3 py-1.5 rounded-full text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70 hover:text-white transition-all duration-300 whitespace-nowrap"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="nav-blog-link"
        >
          Blog
        </Link>

        <button
          onClick={handleLoginClick}
          className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70 hover:text-white border border-white/10 hover:border-white/25 transition-all duration-300 whitespace-nowrap"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="nav-login-btn"
        >
          <LogIn size={12} />
          <span className="hidden sm:inline">Login</span>
        </button>

        <Link
          to="/onboarding"
          onClick={() => trackButtonClick('design_hibot_cta', 'header')}
          className={`bg-[#00FFD1] text-black px-4 sm:px-5 py-1.5 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-[#7DFFD4] transition-all duration-300 flex items-center gap-1.5 group whitespace-nowrap ${
            glowing ? 'shadow-[0_0_20px_rgba(0,255,209,0.6)] scale-105' : ''
          }`}
          style={{ fontFamily: "'Orbitron', sans-serif", transition: 'box-shadow 0.5s ease, transform 0.3s ease' }}
          data-testid="nav-design-hibot"
        >
          <span className="hidden sm:inline">Design your Hi-Bot</span>
          <span className="sm:hidden">Start</span>
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </header>
  );
};
